import { z } from "zod";
import { isPhoneComplete } from "@/shared/utils";
import type { CommercialOrganization } from "../../../types/commercialOrganizations";

export function createPlatformAccessSchema(
  commercialOrganizations: CommercialOrganization[] = [],
  existingOrgId?: number,
) {
  return z
  .object({
    contract_date: z.date().nullable().optional(),
    orgSelectionMode: z.enum(["existing", "create"]).optional(),
    commercial_org_id: z.number().optional(),
    commercial_org_name: z.string().optional(),
    commercial_org_bin: z.string().optional(),
    commercial_org_representative: z.string().optional(),
    commercial_org_basis: z.string().optional(),
    commercial_org_phone: z.string().optional(),
    commercial_org_email: z.string().optional(),
    commercial_org_address: z.string().optional(),
    counterparty_bank_name: z.string(),
    counterparty_iban: z.string(),
    counterparty_bik: z.string(),
  })
  .refine(
    (data) => {
      if (!data.commercial_org_id || data.commercial_org_id === 0) {
        return !!data.commercial_org_name && data.commercial_org_name.trim().length > 0;
      }
      return true;
    },
    {
      message: "validation.legal.commercial_org_name_required",
      path: ["commercial_org_name"],
    },
  )
  .refine(
    (data) => {
      if (!data.commercial_org_id || data.commercial_org_id === 0) {
        return !!data.commercial_org_bin && /^\d{12}$/.test(data.commercial_org_bin);
      }
      return true;
    },
    {
      message: "validation.legal.commercial_org_bin_invalid",
      path: ["commercial_org_bin"],
    },
  )
  .refine(
    (data) => {
      if (!data.commercial_org_id || data.commercial_org_id === 0) {
        return !!data.commercial_org_representative && data.commercial_org_representative.trim().length > 0;
      }
      return true;
    },
    {
      message: "validation.legal.commercial_org_representative_required",
      path: ["commercial_org_representative"],
    },
  )
  .refine(
    (data) => {
      if (!data.commercial_org_id || data.commercial_org_id === 0) {
        return !!data.commercial_org_email && data.commercial_org_email.trim().length > 0;
      }
      return true;
    },
    {
      message: "validation.legal.commercial_org_email_required",
      path: ["commercial_org_email"],
    },
  )
  .refine(
    (data) => !data.commercial_org_phone || isPhoneComplete(data.commercial_org_phone),
    {
      message: "validation.legal.commercial_org_phone_invalid",
      path: ["commercial_org_phone"],
    },
  )
  .refine(
    (data) => {
      if ((!data.commercial_org_id || data.commercial_org_id === 0) && data.commercial_org_bin) {
        const existingOrg = commercialOrganizations.find(
          (org) => org.bin === data.commercial_org_bin && (!existingOrgId || org.id !== existingOrgId),
        );
        return !existingOrg;
      }
      return true;
    },
    {
      message: "validation.legal.commercial_org_bin_duplicate",
      path: ["commercial_org_bin"],
    },
  )
  .refine(
    (data) => {
      if ((!data.commercial_org_id || data.commercial_org_id === 0) && data.commercial_org_name) {
        const existingOrg = commercialOrganizations.find(
          (org) =>
            org.name_ru.toLowerCase().trim() === data.commercial_org_name?.toLowerCase().trim() &&
            (!existingOrgId || org.id !== existingOrgId),
        );
        return !existingOrg;
      }
      return true;
    },
    {
      message: "validation.legal.commercial_org_name_duplicate",
      path: ["commercial_org_name"],
    },
  );
}

export const platformAccessSchema = createPlatformAccessSchema();

export type PlatformAccessFormValues = z.infer<typeof platformAccessSchema>;
